import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react"
import DatePicker, { registerLocale } from "react-datepicker"
import { DateTime } from "luxon"
import { format, isValid, parse } from "date-fns"
import { useTranslation } from "react-i18next"
import { useCommonAndCompanyEvents } from "../../hooks/useCommonAndCompanyEvents.js"
import Tooltip from "@mui/material/Tooltip"

import fi from "date-fns/locale/fi"
import sv from "date-fns/locale/sv"
import enGB from "date-fns/locale/en-GB"
import nl from "date-fns/locale/nl"
import { DatePickerWrapper } from "./datepickerwrapper.jsx"
import { SimpleCalendarIcon } from "../icons.jsx"


registerLocale("fi", fi)
registerLocale("sv", sv)
registerLocale("en", enGB)
registerLocale("nl", nl)

/** Returns the date-fns format string for a given locale */
const getDateFormat = (lang) =>
  lang === "fi" || lang === "nl" ? "dd.MM.yyyy" : "yyyy-MM-dd"

/** Returns the input placeholder string for a given locale */
const getPlaceholder = (lang) =>
  lang === "fi" || lang === "nl" ? "PP.KK.VVVV" : "YYYY-MM-DD"

/**
 * Text input for a date field that supports both free typing and calendar popup selection
 * Validates typed input on blur using the active locale's date format
 */
const DateInput = forwardRef(({ value, onClick, placeholder, onDateChange, dateFormat: fmt, focusRef, onTab }, ref) => {
  const [editingText, setEditingText] = useState(null)
  const [hasError, setHasError] = useState(false)

  // When the calendar sets a new value, clear any in-progress typed text
  useEffect(() => {
    setEditingText(null)
    setHasError(false)
  }, [value])

  const handleChange = (e) => {
    setEditingText(e.target.value)
    setHasError(false)
  }

  const handleBlur = () => {
    if (editingText === null) return
    if (editingText.trim() === "") {
      setEditingText(null)
      onDateChange?.(null)
      return
    }
    const parsed = parse(editingText, fmt, new Date())
    if (isValid(parsed)) {
      setHasError(false)
      setEditingText(null)
      onDateChange?.(parsed)
    } else {
      setHasError(true)
    }
  }

  return (
    <div className="dp-input-wrapper">
    <input
      ref={(el) => {
        if (typeof ref === "function") ref(el); else if (ref) ref.current = el
        if (focusRef) focusRef.current = el
      }}
      type="text"
      className={`dp-half-input${hasError ? " dp-half-input--error" : ""}`}
      value={editingText !== null ? editingText : (value ?? "")}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.target.blur()
        if (e.key === "Tab") onTab?.(e)
      }}
      placeholder={placeholder}
    />
    <span className="dp-calendar-icon" onClick={onClick}>
        <SimpleCalendarIcon />
      </span>
      </div>
  )
})
DateInput.displayName = "DateInput"

/**
 * Date range picker integrated with FullCalendar.
 * Displays two date inputs (start / end) that stay in sync with FullCalendar's visible window.
 * Calls `onApply` immediately when both a valid start and end date are present.
 *
 * @param {{ start: Date, end: Date } | null} appliedRange - The currently applied range, or null when no range is active
 * @param {{ start: string, end: string } | null} viewRange - The currently visible FullCalendar window as ISO date strings
 * @param {(start: Date, end: Date) => void} onApply - Called when a complete, valid range is selected
 */
const toIsoDateKey = (date) => format(date, "yyyy-MM-dd")

export const FullCalendarRangePicker = ({ appliedRange, viewRange, onApply }) => {
  const { i18n, t } = useTranslation()
  const lang = (i18n.resolvedLanguage ?? "fi").split("-")[0]
  const endInputRef = useRef(null)

  const holidayStart = useMemo(() => DateTime.now().minus({ years: 1 }).startOf("year").toISODate(), [])
  const holidayEnd = useMemo(() => DateTime.now().plus({ years: 2 }).endOf("year").toISODate(), [])
  
  const { data: holidays = [] } = useCommonAndCompanyEvents({ start: holidayStart, end: holidayEnd })
  
  const holidayByIso = useMemo(() => {
    const map = new Map()
    for (const holiday of holidays) {
      if (holiday?.date) map.set(holiday.date, holiday)
    }
    return map
  }, [holidays])

  const holidayTooltipByIso = useMemo(() => {
    const map = new Map()
    for (const holiday of holidays) {
      if (!holiday?.date || !holiday?.descKey) continue
      map.set(holiday.date, t(holiday.descKey))
    }
    return map
  }, [holidays, t])

  // Convert ISO string from viewRange (FullCalendar endStr is exclusive — subtract 1 day)
  const viewStart = viewRange ? new Date(viewRange.start + "T00:00:00") : null
  const viewEnd = viewRange ? DateTime.fromISO(viewRange.end).minus({ days: 1 }).toJSDate() : null

  const [startDate, setStartDate] = useState(appliedRange?.start ?? viewStart)
  const [endDate, setEndDate] = useState(appliedRange?.end ?? viewEnd)

  // Keep local state in sync when appliedRange or viewRange changes externally
  useEffect(() => {
    if (appliedRange) {
      setStartDate(appliedRange.start)
      setEndDate(appliedRange.end)
    } else {
      setStartDate(viewStart)
      setEndDate(viewEnd)
    }
  }, [appliedRange, viewRange])

  const handleStartChange = (date) => {
    setStartDate(date)
    // Only clear end if it's now before the new start (invalid range)
    if (endDate && date && date > endDate) {
      setEndDate(null)
    } else if (date && endDate) {
      onApply(date, endDate)
    }
  }

  const handleEndChange = (date) => {
    setEndDate(date)
    if (startDate && date && date < startDate) {
      setStartDate(null)
    } else if (startDate && date) {
      onApply(startDate, date)
    }
  }

  const fmt = getDateFormat(lang)
  const placeholder = getPlaceholder(lang)

  const renderCustomHeader = useCallback(({ date, decreaseMonth, increaseMonth }) => (
    <div className="dp-custom-header">
      <button type="button" className="dp-nav-btn" onClick={decreaseMonth}>‹</button>
      <span className="dp-header-label">
        {DateTime.fromJSDate(date).setLocale(lang).toFormat("MMMM yyyy")}
      </span>
      <button type="button" className="dp-nav-btn" onClick={increaseMonth}>›</button>
    </div>
  ), [lang])

  const dayClassName = useCallback((date) => {
    const classes = []
    const iso = toIsoDateKey(date)
    if (iso && holidayByIso.has(iso)) classes.push('holiday')

    return classes.length > 0 ? classes.join(' ') : undefined
  }, [holidayByIso])

  const renderDayContents = useCallback((day, date) => {
    if (!date) return day
    const iso = toIsoDateKey(date)
    const title = iso ? holidayTooltipByIso.get(iso) : undefined
    if (!title) return day

    return <Tooltip title={title}><span>{day}</span></Tooltip>
  }, [holidayTooltipByIso])

  return (
    <DatePickerWrapper className="dp-range-mode">
      <div className="dp-dual-row">
        <div className="dp-range-input-wrapper">
          <div className="dp-date-field">
            <DatePicker
              selected={startDate}
              onChange={handleStartChange}
              selectsStart
              startDate={startDate}
              endDate={endDate}
              dayClassName={dayClassName}
              renderDayContents={renderDayContents}
              locale={lang}
              dateFormat={fmt}
              placeholderText={placeholder}
              renderCustomHeader={renderCustomHeader}
              customInput={
                <DateInput
                  placeholder={placeholder}
                  onDateChange={handleStartChange}
                  dateFormat={fmt}
                  onTab={(e) => { e.preventDefault(); endInputRef.current?.focus() }}
                />
              }
            />
          </div>
          <span className="dp-range-sep">–</span>
          <div className="dp-date-field">
            <DatePicker
              selected={endDate}
              onChange={handleEndChange}
              selectsEnd
              startDate={startDate}
              endDate={endDate}
              minDate={startDate}
              dayClassName={dayClassName}
              renderDayContents={renderDayContents}
              locale={lang}
              dateFormat={fmt}
              placeholderText={placeholder}
              renderCustomHeader={renderCustomHeader}
              customInput={
                <DateInput
                  placeholder={placeholder}
                  onDateChange={handleEndChange}
                  dateFormat={fmt}
                  focusRef={endInputRef}
                />
              }
            />
          </div>
        </div>
      </div>
    </DatePickerWrapper>
  )
}
